<script>
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { api } from '../lib/api.js';
  import MethodChip from '../components/MethodChip.svelte';
  import { ui } from '../lib/ui.svelte.js';

  let { id } = $props();
  let cap = $state(null);
  let err = $state('');

  onMount(async () => {
    try { cap = await api.getCapture(id); }
    catch (e) { err = e.message; }
  });

  function fmt(t) { return new Date(t).toLocaleString(); }

  function decodedBody(c) {
    if (!c.body) return '';
    if (c.body_base64) {
      try { return atob(c.body); } catch { return c.body; }
    }
    return c.body;
  }

  function copyURL() {
    const base = window.location.origin;
    const path = cap.bucket_id ? `/h/${cap.bucket_id}` : '/h';
    navigator.clipboard.writeText(base + path + cap.path);
  }

  async function del() {
    const ok = await ui.ask({
      title: 'Delete capture?',
      message: 'This capture will be permanently removed.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try { await api.deleteCapture(id); ui.snack('Capture deleted', 'success'); navigate('/captures'); }
    catch (e) { ui.snack(e.message, 'error'); }
  }
</script>

{#if err}
  <p class="text-error">{err}</p>
{:else if !cap}
  <p class="text-on-surface-variant">Loading...</p>
{:else}
  <div class="flex items-center gap-3 mb-4">
    <button onclick={() => history.back()} class="p-2 rounded-full hover:bg-surface-container-high">
      <span class="material-symbols-outlined">arrow_back</span>
    </button>
    <MethodChip method={cap.method} />
    <h2 class="font-mono text-lg">{cap.path || '/'}</h2>
    <div class="ml-auto flex gap-2">
      <button onclick={copyURL} class="px-4 py-2 rounded-full text-sm hover:bg-surface-container-high">
        <span class="material-symbols-outlined text-base align-middle">content_copy</span>
        Copy URL
      </button>
      <button onclick={del} class="px-4 py-2 rounded-full text-sm text-error hover:bg-error/10">
        <span class="material-symbols-outlined text-base align-middle">delete</span>
        Delete
      </button>
    </div>
  </div>

  <div class="grid md:grid-cols-3 gap-4">
    <section class="bg-surface-container rounded-2xl p-4 md:col-span-1 space-y-2 text-sm">
      <h3 class="text-on-surface-variant uppercase text-xs tracking-wide mb-2">Metadata</h3>
      <div><span class="text-on-surface-variant">Bucket:</span> {cap.bucket_id || 'global'}</div>
      <div><span class="text-on-surface-variant">Received:</span> {fmt(cap.created_at)}</div>
      <div><span class="text-on-surface-variant">Remote:</span> {cap.remote_addr}</div>
      <div><span class="text-on-surface-variant">HTTP:</span> {cap.proto || '—'}</div>
      <div><span class="text-on-surface-variant">User-Agent:</span> {cap.user_agent || '—'}</div>
      <div><span class="text-on-surface-variant">Subject:</span> {cap.subject || '—'}</div>
      <div><span class="text-on-surface-variant">Size:</span> {cap.body_size} bytes {cap.body_truncated ? '(truncated)' : ''}</div>
      {#if cap.query}
        <div><span class="text-on-surface-variant">Query:</span> <code class="font-mono text-xs">{cap.query}</code></div>
      {/if}
    </section>

    <section class="bg-surface-container rounded-2xl p-4 md:col-span-2">
      <h3 class="text-on-surface-variant uppercase text-xs tracking-wide mb-2">Headers</h3>
      <table class="w-full text-sm font-mono">
        <tbody>
          {#each Object.entries(cap.headers) as [k, vs]}
            <tr class="border-b border-outline-variant/50">
              <td class="py-1 pr-4 text-on-surface-variant align-top whitespace-nowrap">{k}</td>
              <td class="py-1 break-all">{vs.join(', ')}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </section>

    <section class="bg-surface-container rounded-2xl p-4 md:col-span-3">
      <h3 class="text-on-surface-variant uppercase text-xs tracking-wide mb-2">
        Body {cap.content_type ? `(${cap.content_type})` : ''}
      </h3>
      <pre class="bg-surface rounded-xl p-4 overflow-auto text-xs font-mono whitespace-pre-wrap break-all">{decodedBody(cap)}</pre>
    </section>
  </div>
{/if}
